import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Wallet, ChevronRight, ArrowLeft, Plus, Pencil, X, AlertTriangle,
  CheckCircle2, Receipt, Trash2, RotateCcw, Search,
} from "lucide-react";

/* eslint-disable react-hooks/exhaustive-deps */

const PLAN_LABEL = { full: "Full / Annual", semi_annual: "Semi-Annual", trimester: "Trimester", quarterly: "Quarterly", monthly: "Monthly" };
const STATUS_PILL = {
  paid:      { label: "Paid",      cls: "bg-[#E8F5E9] text-[#2E7D32] border-[#2E7D32]/30" },
  upcoming:  { label: "Upcoming",  cls: "bg-[#F3F4F6] text-[#374151] border-[#9CA3AF]/30" },
  due_soon:  { label: "Due Soon",  cls: "bg-[#FEF9C3] text-[#854D0E] border-[#F59E0B]/40" },
  very_soon: { label: "Due Very Soon", cls: "bg-[#FFEDD5] text-[#9A3412] border-[#EA580C]/40" },
  overdue:   { label: "Overdue",   cls: "bg-[#FDECEA] text-[#C62828] border-[#C62828]/30" },
};
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "online", label: "Online Payment" },
  { value: "card", label: "Debit / Credit Card" },
  { value: "check", label: "Bank Check" },
  { value: "other", label: "Others" },
];
const peso = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function TuitionPlansPage() {
  const { selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const branchId = selectedBranch?.id;

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Drilldown
  const [drilldown, setDrilldown] = useState(null); // student row
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Generate dialog
  const [genFor, setGenFor] = useState(null);
  const [genPlan, setGenPlan] = useState("monthly");
  const [genStart, setGenStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [genMonths, setGenMonths] = useState(10);
  const [genOverwrite, setGenOverwrite] = useState(false);

  // Edit installment
  const [editing, setEditing] = useState(null); // installment
  const [editDate, setEditDate] = useState("");
  const [editAmount, setEditAmount] = useState("");

  // Add payment
  const [paying, setPaying] = useState(null);
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payNotes, setPayNotes] = useState("");
  const [payReferenceNo, setPayReferenceNo] = useState("");

  // Delete & Regenerate-remaining
  const [deleteInfo, setDeleteInfo] = useState(null); // { paid_installments_count, paid_total, remaining_balance, force? }
  const [regenForm, setRegenForm] = useState(null); // { plan_type, start_date, months_remaining, total_remaining }

  // Load lookups
  useEffect(() => {
    if (!branchId) { setClasses([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/classes", { params: { branch_id: branchId } });
        setClasses((data || []).filter((c) => c.class_type === "regular" || c.class_type === "modular"));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId]);

  useEffect(() => {
    setSectionId("");
    if (!classId) { setSections([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/sections", { params: { branch_id: branchId, class_id: classId } });
        setSections(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [branchId, classId]);

  const loadRows = useCallback(async () => {
    if (!branchId) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/tuition-schedules", {
        params: { branch_id: branchId, class_id: classId || undefined, section_id: sectionId || undefined },
      });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId, classId, sectionId]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const loadSchedule = useCallback(async (studentId) => {
    if (!branchId || !studentId) return;
    setScheduleLoading(true);
    try {
      const { data } = await api.get(`/tuition-schedules/by-student/${studentId}`, { params: { branch_id: branchId } });
      setSchedule(data || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setScheduleLoading(false); }
  }, [branchId]);

  const openDrilldown = (row) => { setDrilldown(row); loadSchedule(row.student_id); };
  const closeDrilldown = () => { setDrilldown(null); setSchedule(null); };

  /* Generate */
  const submitGenerate = async () => {
    try {
      await api.post("/tuition-schedules/generate", {
        branch_id: branchId, student_id: genFor.student_id,
        plan_type: genPlan, start_date: genStart,
        school_year_months: Number(genMonths) || 10, overwrite: genOverwrite,
      });
      toast.success("Schedule generated");
      setGenFor(null);
      loadRows();
      if (drilldown?.student_id === genFor.student_id) loadSchedule(genFor.student_id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* Edit row */
  const openEdit = (inst) => { setEditing(inst); setEditDate(inst.due_date); setEditAmount(String(inst.amount_due)); };
  const submitEdit = async () => {
    try {
      await api.patch(`/tuition-schedules/${schedule.id}/installments/${editing.id}`, {
        due_date: editDate, amount_due: Number(editAmount),
      });
      toast.success("Updated");
      setEditing(null);
      loadSchedule(drilldown.student_id); loadRows();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* Add payment */
  const openPay = (inst) => {
    setPaying(inst); setPayDate(new Date().toISOString().slice(0, 10));
    setPayAmount(String(Math.max(0, (inst.amount_due || 0) - (inst.paid_total || 0))));
    setPayMethod("cash"); setPayNotes(""); setPayReferenceNo("");
  };
  const submitPay = async () => {
    try {
      await api.post("/tuition-payments", {
        branch_id: branchId, schedule_id: schedule.id, installment_id: paying.id,
        date: payDate, amount: Number(payAmount), method: payMethod,
        notes: payNotes, reference_no: payReferenceNo.trim(),
      });
      toast.success("Payment recorded");
      setPaying(null);
      loadSchedule(drilldown.student_id); loadRows();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deletePayment = async (pid) => {
    if (!window.confirm("Reverse this payment?")) return;
    try {
      await api.delete(`/tuition-payments/${pid}`);
      toast.success("Payment reversed");
      loadSchedule(drilldown.student_id); loadRows();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* Delete schedule — protected by paid installments */
  const handleDeleteSchedule = async (force = false) => {
    try {
      await api.delete(`/tuition-schedules/${schedule.id}${force ? "?force=true" : ""}`);
      toast.success(force ? "Schedule force-deleted" : "Schedule deleted");
      setDeleteInfo(null);
      closeDrilldown(); loadRows();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof d === "object") {
        setDeleteInfo(d);
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const openRegen = () => {
    setRegenForm({
      plan_type: "monthly",
      start_date: new Date().toISOString().slice(0, 10),
      months_remaining: 6,
      total_remaining: deleteInfo?.remaining_balance ?? schedule?.total_balance ?? 0,
    });
    setDeleteInfo(null);
  };
  const submitRegen = async () => {
    try {
      await api.post(`/tuition-schedules/${schedule.id}/regenerate-remaining`, {
        plan_type: regenForm.plan_type,
        start_date: regenForm.start_date,
        months_remaining: Number(regenForm.months_remaining),
        total_remaining_amount: Number(regenForm.total_remaining),
      });
      toast.success("Remaining schedule regenerated — paid installments preserved");
      setRegenForm(null);
      loadSchedule(drilldown.student_id); loadRows();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const summary = useMemo(() => {
    const t = { paid: 0, upcoming: 0, due_soon: 0, very_soon: 0, overdue: 0, no_schedule: 0 };
    for (const r of rows) {
      if (!r.has_schedule) { t.no_schedule++; continue; }
      for (const [k, v] of Object.entries(r.status_counts || {})) t[k] = (t[k] || 0) + v;
    }
    return t;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.student_name || "").toLowerCase().includes(q));
  }, [rows, query]);

  if (drilldown) {
    return (
      <>
        <button onClick={closeDrilldown} className="text-brand-purple inline-flex items-center gap-1 text-sm mb-3" data-testid="tp-back">
          <ArrowLeft className="w-4 h-4" /> Back to all students
        </button>
        <div className="mb-4">
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{drilldown.student_name}</h1>
          <p className="text-sm text-brand-inkMuted">Tuition Schedule & Payment History</p>
        </div>

        {scheduleLoading ? <p>Loading...</p>
        : !schedule ? (
          <div className="bg-white rounded-2xl border border-brand-purple/10 p-12 text-center" data-testid="tp-no-schedule">
            <Wallet className="w-12 h-12 mx-auto text-brand-purple mb-3" />
            <p className="font-heading text-lg font-semibold">No schedule yet</p>
            <p className="text-sm text-brand-inkMuted mt-1">Generate a tuition schedule to start tracking payments.</p>
            <Button onClick={() => setGenFor(drilldown)} className="mt-4 rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="tp-generate-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Generate Schedule
            </Button>
          </div>
        ) : (
          <>
            <section className="bg-white rounded-2xl border border-brand-purple/10 p-4 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="tp-summary">
              <Stat label="Plan" value={PLAN_LABEL[schedule.plan_type]} />
              <Stat label="Total Due" value={peso(schedule.total_amount_due)} />
              <Stat label="Paid" value={peso(schedule.total_paid)} tone="green" />
              <Stat label="Balance" value={peso(schedule.total_balance)} tone={schedule.total_balance > 0 ? "red" : "green"} />
            </section>

            <div className="flex justify-end mb-3 gap-2">
              <Button onClick={() => handleDeleteSchedule(false)} variant="outline"
                className="rounded-2xl text-[#C62828] border-[#C62828]/40 hover:bg-[#FDECEA]" data-testid="tp-delete-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete Schedule
              </Button>
              <Button onClick={() => setGenFor(drilldown)} variant="outline" className="rounded-2xl" data-testid="tp-regen-btn">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Regenerate (overwrite)
              </Button>
            </div>

            <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-x-auto mb-4" data-testid="tp-installments">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-brand-purpleLight/40 text-foreground">
                    <th className="text-left px-3 py-2 font-heading">#</th>
                    <th className="text-left px-3 py-2 font-heading">Due Date</th>
                    <th className="text-left px-3 py-2 font-heading">Label</th>
                    <th className="text-right px-3 py-2 font-heading">Amount Due</th>
                    <th className="text-right px-3 py-2 font-heading">Paid</th>
                    <th className="text-right px-3 py-2 font-heading">Balance</th>
                    <th className="px-3 py-2 font-heading">Status</th>
                    <th className="text-right px-3 py-2 font-heading">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.installments.map((inst, i) => {
                    const pill = STATUS_PILL[inst.status] || STATUS_PILL.upcoming;
                    return (
                      <tr key={inst.id} className="border-t border-brand-purple/10" data-testid={`tp-row-${inst.id}`}>
                        <td className="px-3 py-2 text-brand-inkMuted">{i + 1}</td>
                        <td className="px-3 py-2 font-heading">{inst.due_date}</td>
                        <td className="px-3 py-2">{inst.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{peso(inst.amount_due)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-[#2E7D32]">{peso(inst.paid_total)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-heading font-semibold">{peso(inst.balance)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${pill.cls}`} data-testid={`tp-status-${inst.id}`}>
                            {pill.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => openEdit(inst)} className="p-1.5 rounded-lg hover:bg-brand-purpleLight/40" title="Edit due date/amount" data-testid={`tp-edit-${inst.id}`}>
                            <Pencil className="w-4 h-4 text-brand-purple" />
                          </button>
                          {inst.balance > 0 && (
                            <button onClick={() => openPay(inst)} className="ml-1 inline-flex items-center gap-1 text-xs font-heading font-semibold rounded-lg bg-brand-purple hover:bg-brand-purpleHover text-white px-2 py-1.5" data-testid={`tp-pay-${inst.id}`}>
                              <Plus className="w-3 h-3" /> Pay
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>

            {/* Payment history */}
            <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-x-auto" data-testid="tp-history">
              <div className="px-4 py-3 border-b border-brand-purple/10">
                <p className="font-heading text-sm font-semibold">Payment History</p>
              </div>
              {(schedule.payments || []).length === 0
                ? <p className="px-4 py-6 text-sm text-brand-inkMuted text-center">No payments recorded yet.</p>
                : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-brand-purpleLight/20">
                        <th className="text-left px-3 py-2 font-heading">Date</th>
                        <th className="text-right px-3 py-2 font-heading">Amount</th>
                        <th className="text-left px-3 py-2 font-heading">Method</th>
                        <th className="text-left px-3 py-2 font-heading">Notes</th>
                        <th className="text-left px-3 py-2 font-heading">Recorded By</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.payments.map((p) => (
                        <tr key={p.id} className="border-t border-brand-purple/10" data-testid={`tp-pay-row-${p.id}`}>
                          <td className="px-3 py-2">{p.date}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-heading">{peso(p.amount)}</td>
                          <td className="px-3 py-2 capitalize">{p.method.replace("_", " ")}</td>
                          <td className="px-3 py-2 text-brand-inkMuted">{p.notes || "—"}</td>
                          <td className="px-3 py-2 text-brand-inkMuted">{p.recorded_by_name}</td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => deletePayment(p.id)} className="p-1.5 text-[#C62828] hover:bg-[#FDECEA] rounded-lg" title="Reverse">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </section>
          </>
        )}

        {/* Dialogs */}
        {genFor && <GenerateDialog
          studentName={genFor.student_name} planType={genPlan} setPlanType={setGenPlan}
          startDate={genStart} setStartDate={setGenStart}
          months={genMonths} setMonths={setGenMonths}
          overwrite={genOverwrite} setOverwrite={setGenOverwrite}
          hasExisting={genFor.has_schedule}
          onCancel={() => setGenFor(null)} onSubmit={submitGenerate}
        />}
        {editing && <EditDialog
          installment={editing} date={editDate} setDate={setEditDate}
          amount={editAmount} setAmount={setEditAmount}
          onCancel={() => setEditing(null)} onSubmit={submitEdit}
        />}
        {paying && <PaymentDialog
          installment={paying} date={payDate} setDate={setPayDate}
          amount={payAmount} setAmount={setPayAmount} method={payMethod} setMethod={setPayMethod}
          notes={payNotes} setNotes={setPayNotes}
          referenceNo={payReferenceNo} setReferenceNo={setPayReferenceNo}
          onCancel={() => setPaying(null)} onSubmit={submitPay}
        />}

        {deleteInfo && (
          <Modal title="Cannot fully delete this schedule"
            subtitle={`${deleteInfo.paid_installments_count} installment(s) already have payments`}
            onClose={() => setDeleteInfo(null)} testId="tp-delete-blocked-dialog">
            <div className="rounded-xl border border-[#F59E0B]/40 bg-[#FFF7E6] p-3 text-sm space-y-1">
              <p>💰 <strong>Already paid:</strong> ₱{Number(deleteInfo.paid_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              <p>⏳ <strong>Unpaid balance:</strong> ₱{Number(deleteInfo.remaining_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
            <p className="text-sm text-brand-inkMuted">Paid installments cannot be removed. You have two options:</p>
            <div className="space-y-2">
              <Button onClick={openRegen} className="w-full justify-start rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
                data-testid="tp-delete-regen-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> Keep paid · Regenerate remaining balance over new months
              </Button>
              <Button onClick={() => handleDeleteSchedule(true)} variant="outline"
                className="w-full justify-start rounded-2xl text-[#C62828] border-[#C62828]/40 hover:bg-[#FDECEA]"
                data-testid="tp-delete-force-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Force-delete EVERYTHING (paid installments + payment history)
              </Button>
            </div>
            <p className="text-xs text-brand-inkMuted">Force-delete cannot be undone. Use it only for genuinely wrong / test schedules.</p>
          </Modal>
        )}

        {regenForm && (
          <Modal title="Regenerate Remaining Payment Plan"
            subtitle={`Paid installments will be preserved`}
            onClose={() => setRegenForm(null)} testId="tp-regen-remaining-dialog">
            <Field label="REMAINING BALANCE (₱)">
              <Input type="number" step="0.01" value={regenForm.total_remaining}
                onChange={(e) => setRegenForm({ ...regenForm, total_remaining: e.target.value })}
                className="rounded-xl h-11" data-testid="tp-regen-amount" />
              <p className="text-xs text-brand-inkMuted mt-1">Pre-filled from unpaid balance. Edit only if you negotiated a different total.</p>
            </Field>
            <Field label="NEW PLAN TYPE">
              <select value={regenForm.plan_type}
                onChange={(e) => setRegenForm({ ...regenForm, plan_type: e.target.value })}
                className="tp-select" data-testid="tp-regen-plan">
                <option value="monthly">Monthly</option>
                <option value="trimester">Trimester (3 payments)</option>
                <option value="quarterly">Quarterly (4 payments, 2.5 mo each)</option>
                <option value="semi_annual">Semi-Annual (2 payments)</option>
                <option value="full">Full / Annual (1 payment)</option>
              </select>
            </Field>
            <Field label="START DATE">
              <Input type="date" value={regenForm.start_date}
                onChange={(e) => setRegenForm({ ...regenForm, start_date: e.target.value })}
                className="rounded-xl h-11" data-testid="tp-regen-start" />
            </Field>
            <Field label="MONTHS REMAINING">
              <Input type="number" min={1} max={12} value={regenForm.months_remaining}
                onChange={(e) => setRegenForm({ ...regenForm, months_remaining: e.target.value })}
                className="rounded-xl h-11" data-testid="tp-regen-months" />
            </Field>
            <ModalActions onCancel={() => setRegenForm(null)} onSubmit={submitRegen}
              submitLabel="Regenerate Remaining" testId="tp-regen-submit" />
          </Modal>
        )}
      </>
    );
  }

  // List view
  return (
    <>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Tuition Plans</h1>
        <p className="text-sm text-brand-inkMuted mt-0.5">Auto-generate payment schedules and record payments for regular-class students.</p>
      </div>

      {/* Summary tiles */}
      <section className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4" data-testid="tp-tiles">
        <Tile label="Overdue" value={summary.overdue} pill="overdue" />
        <Tile label="Due ≤3 days" value={summary.very_soon} pill="very_soon" />
        <Tile label="Due ≤5 days" value={summary.due_soon} pill="due_soon" />
        <Tile label="Upcoming" value={summary.upcoming} pill="upcoming" />
        <Tile label="Paid" value={summary.paid} pill="paid" />
        <Tile label="No Schedule" value={summary.no_schedule} pill="upcoming" />
      </section>

      {/* Filters */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="BRANCH">
            <select value={branchId || ""} onChange={(e) => setSelectedBranchId(e.target.value)} className="tp-select" data-testid="tp-branch">
              {branchesForSelectedSchool.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="CLASS / LEVEL">
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className="tp-select" data-testid="tp-class">
              <option value="">All levels</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="SECTION">
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId} className="tp-select" data-testid="tp-section">
              <option value="">All sections</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="SEARCH STUDENT">
            <div className="relative">
              <Search className="w-4 h-4 text-brand-inkMuted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type student name..."
                className="rounded-xl h-11 pl-9"
                data-testid="tp-search"
              />
            </div>
          </Field>
        </div>
      </section>

      {/* Students table */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-x-auto" data-testid="tp-list">
        {loading ? <p className="p-6 text-brand-inkMuted">Loading...</p>
        : rows.length === 0 ? <p className="p-6 text-brand-inkMuted text-center">No regular-class students in this scope.</p>
        : filteredRows.length === 0 ? <p className="p-6 text-brand-inkMuted text-center">No students match &quot;{query}&quot;.</p>
        : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-purpleLight/40">
                <th className="text-left px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Student</th>
                <th className="text-left px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Plan</th>
                <th className="text-right px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Total Due</th>
                <th className="text-right px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Paid</th>
                <th className="text-right px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Balance</th>
                <th className="px-4 py-3 font-heading text-[11px] uppercase tracking-wider">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const worst = ["overdue", "very_soon", "due_soon", "upcoming", "paid"].find(
                  (s) => (r.status_counts?.[s] || 0) > 0
                ) || "upcoming";
                const pill = STATUS_PILL[worst];
                return (
                  <tr key={r.student_id} className="border-t border-brand-purple/10 hover:bg-brand-purpleLight/20 cursor-pointer" onClick={() => openDrilldown(r)} data-testid={`tp-stu-${r.student_id}`}>
                    <td className="px-4 py-3 font-heading">{r.student_name}</td>
                    <td className="px-4 py-3 text-brand-inkMuted">{r.has_schedule ? PLAN_LABEL[r.plan_type] : "— No plan —"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.has_schedule ? peso(r.total_amount_due) : "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#2E7D32]">{r.has_schedule ? peso(r.total_paid) : "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-heading font-semibold">{r.has_schedule ? peso(r.total_balance) : "—"}</td>
                    <td className="px-4 py-3">
                      {r.has_schedule
                        ? <span className={`inline-flex items-center text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${pill.cls}`}>{pill.label}</span>
                        : <span className="text-xs text-brand-inkMuted italic">no schedule</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {!r.has_schedule && (
                        <button onClick={(e) => { e.stopPropagation(); setGenFor(r); setGenPlan("monthly"); setGenStart(new Date().toISOString().slice(0, 10)); setGenMonths(10); setGenOverwrite(false); }}
                          className="inline-flex items-center gap-1 text-xs font-heading font-semibold rounded-lg bg-brand-purple hover:bg-brand-purpleHover text-white px-2.5 py-1.5"
                          data-testid={`tp-gen-${r.student_id}`}>
                          <Plus className="w-3 h-3" /> Generate
                        </button>
                      )}
                      <ChevronRight className="w-4 h-4 text-brand-inkMuted inline ml-2" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {genFor && <GenerateDialog
        studentName={genFor.student_name} planType={genPlan} setPlanType={setGenPlan}
        startDate={genStart} setStartDate={setGenStart}
        months={genMonths} setMonths={setGenMonths}
        overwrite={genOverwrite} setOverwrite={setGenOverwrite}
        hasExisting={genFor.has_schedule}
        onCancel={() => setGenFor(null)} onSubmit={submitGenerate}
      />}

      <style>{`
        .tp-select { width:100%; height:2.75rem; border-radius:0.75rem; border:1px solid rgba(37,99,235,.2); background:#fff; padding:0 .75rem; font-size:.9rem; color:hsl(var(--foreground)); }
      `}</style>
    </>
  );
}

/* ---------- Subcomponents ---------- */
function Field({ label, children }) {
  return (<div><p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p><div className="mt-1.5">{children}</div></div>);
}
function Stat({ label, value, tone }) {
  const tones = { green: "text-[#2E7D32]", red: "text-[#C62828]" };
  return (<div><p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p><p className={`font-heading text-lg font-bold tabular-nums mt-0.5 ${tones[tone] || "text-foreground"}`}>{value}</p></div>);
}
function Tile({ label, value, pill }) {
  const p = STATUS_PILL[pill] || STATUS_PILL.upcoming;
  return (<div className={`rounded-2xl border ${p.cls} p-3`}><p className="text-[10px] uppercase tracking-wider font-heading font-bold opacity-80">{label}</p><p className="font-heading text-2xl font-bold tabular-nums mt-1">{value}</p></div>);
}

function GenerateDialog({ studentName, planType, setPlanType, startDate, setStartDate, months, setMonths, overwrite, setOverwrite, hasExisting, onCancel, onSubmit }) {
  return (
    <Modal title="Generate Tuition Schedule" subtitle={`For: ${studentName}`} onClose={onCancel} testId="tp-gen-dialog">
      <Field label="PLAN TYPE">
        <select value={planType} onChange={(e) => setPlanType(e.target.value)} className="tp-select" data-testid="tp-gen-plan">
          <option value="monthly">Monthly (10 payments)</option>
          <option value="trimester">Trimester (3 payments, ~3.33 mo each)</option>
          <option value="quarterly">Quarterly (4 payments, 2.5 mo each)</option>
          <option value="semi_annual">Semi-Annual (2 payments)</option>
          <option value="full">Full / Annual (1 payment)</option>
        </select>
      </Field>
      <Field label="START DATE">
        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl h-11" data-testid="tp-gen-start" />
      </Field>
      <Field label="SCHOOL YEAR LENGTH (MONTHS)">
        <Input type="number" min={1} max={12} value={months} onChange={(e) => setMonths(e.target.value)} className="rounded-xl h-11" data-testid="tp-gen-months" />
      </Field>
      {hasExisting && (
        <label className="flex items-center gap-2 mt-3 text-sm">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="tp-gen-overwrite" />
          <span className="text-[#C62828] font-heading font-semibold">Overwrite existing schedule (deletes existing payments)</span>
        </label>
      )}
      <ModalActions onCancel={onCancel} onSubmit={onSubmit} submitLabel="Generate" testId="tp-gen-submit" />
    </Modal>
  );
}

function EditDialog({ installment, date, setDate, amount, setAmount, onCancel, onSubmit }) {
  return (
    <Modal title="Edit Installment" subtitle={installment.label} onClose={onCancel} testId="tp-edit-dialog">
      <Field label="DUE DATE"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl h-11" data-testid="tp-edit-date" /></Field>
      <Field label="AMOUNT DUE"><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl h-11" data-testid="tp-edit-amount" /></Field>
      <p className="text-xs text-brand-inkMuted mt-2">Already paid: ₱{Number(installment.paid_total || 0).toFixed(2)}. Cannot reduce amount below paid total.</p>
      <ModalActions onCancel={onCancel} onSubmit={onSubmit} submitLabel="Save" testId="tp-edit-submit" />
    </Modal>
  );
}

function PaymentDialog({ installment, date, setDate, amount, setAmount, method, setMethod, notes, setNotes, referenceNo, setReferenceNo, onCancel, onSubmit }) {
  return (
    <Modal title="Add Payment" subtitle={`${installment.label} · Balance ₱${Number(installment.amount_due - installment.paid_total).toFixed(2)}`} onClose={onCancel} testId="tp-pay-dialog">
      <Field label="DATE OF PAYMENT"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl h-11" data-testid="tp-pay-date" /></Field>
      <Field label="AMOUNT"><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl h-11" data-testid="tp-pay-amount" /></Field>
      <Field label="PAYMENT METHOD">
        <select value={method} onChange={(e) => setMethod(e.target.value)} className="tp-select" data-testid="tp-pay-method">
          {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </Field>
      <Field label="REFERENCE NO.">
        <Input
          value={referenceNo}
          onChange={(e) => setReferenceNo(e.target.value)}
          className="rounded-xl h-11"
          placeholder="GCash txn ID / cheque # / bank slip #"
          data-testid="tp-pay-reference-no"
        />
      </Field>
      <Field label="NOTES"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-xl border border-brand-purple/20 p-3 text-sm" placeholder="Payer name, additional context, etc." data-testid="tp-pay-notes" /></Field>
      <p className="text-xs text-brand-inkMuted mt-2">&ldquo;Recorded by&rdquo; will be auto-filled with your name. Reference No. surfaces in Accounting → Income.</p>
      <ModalActions onCancel={onCancel} onSubmit={onSubmit} submitLabel="Record Payment" testId="tp-pay-submit" />
    </Modal>
  );
}

function Modal({ title, subtitle, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testId}>
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-brand-purple/10 flex items-start justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground">{title}</h2>
            {subtitle && <p className="text-xs text-brand-inkMuted mt-0.5">{subtitle}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
      </div>
    </div>
  );
}
function ModalActions({ onCancel, onSubmit, submitLabel, testId }) {
  return (
    <div className="flex justify-end gap-2 pt-2 border-t border-brand-purple/10 mt-3 -mb-2">
      <Button variant="outline" onClick={onCancel} className="rounded-2xl">Cancel</Button>
      <Button onClick={onSubmit} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid={testId}>{submitLabel}</Button>
    </div>
  );
}
